/**
 * Marketing i18n fragment — intgFcAgent (integrations/fc-agent).
 *
 * Claims discipline (docs/ai/fixcontrol-website-claims.md): every
 * outbound-only / credentials-stay-local claim on this page is scoped to the
 * FC Agent connection model — never phrased as an absolute over every
 * connection profile. No mTLS / client-certificate / short-lived-token
 * claims (not built): the transport is HMAC-authenticated HTTPS and the
 * operations are Ed25519-signed with a pinned key.
 *
 * The agent is no longer "offered as part of pilot onboarding": since
 * 2026-09-11 the install package is a public, signed release a customer
 * downloads and installs themselves (docs/PLAN-fc-agent-distribution.md), and
 * the `install` block below describes that. What stays scoped is the GATE
 * programme — CI/CD deployment gates remain a per-tenant opt-in, and the
 * capability cards keep saying "where configured".
 */

export const intgFcAgentEn = {
  meta: {
    title: "FC Agent — connect private infrastructure without exposing it",
    description:
      "The FC Agent runs inside your environment and connects outbound to FixControl over HTTPS. Kubernetes, Argo, Jenkins and GitLab stay private: no inbound access, signed single-use operations, customer-side allowlists and least-privilege RBAC. Download the signed package and install it yourself.",
  },
  hero: {
    eyebrow: "FC Agent · Private infrastructure",
    title: "Your infrastructure stays private.",
    highlight: "The FC Agent only dials out.",
    subtitle:
      "The FC Agent is a small service you run in your own environment. It connects outbound to FixControl over HTTPS and executes only signed, expiring, single-use operations — so FixControl can govern deployments and validation on Kubernetes, Argo, Jenkins and GitLab without any of those systems being exposed publicly. You download the package, verify its signature and install it yourself.",
    primaryCta: "Book a demo",
    secondaryCta: "Read the security model",
    bullet1: "Outbound-only — one egress rule",
    bullet2: "Agent connections: credentials stay with you",
    bullet3: "You set the local allowlists",
  },
  diagram: {
    eyebrow: "The connection model",
    title: "One outbound connection, nothing inbound",
    cloud: "FixControl Cloud",
    cloudSub: "gates, approvals, evidence, audit",
    arrow: "Outbound HTTPS — initiated by the agent",
    agent: "FC Agent",
    agentSub: "runs in your environment",
    k8s: "Kubernetes",
    argo: "Argo Rollouts",
    ci: "Jenkins / GitLab",
    caption:
      "The complete firewall requirement is one egress rule. There are no inbound rules, no VPN and no tunnel — the agent verifies each operation locally, then executes it inside your network.",
  },
  how: {
    eyebrow: "How it works",
    title: "From a paused pipeline to an executed decision",
    step1: {
      tag: "Pause",
      title: "Your pipeline pauses itself",
      body: "A deployment reaches a step your team already configured to wait: a GitLab deployment approval or manual job, a Jenkins input step, a GitHub Actions environment review, or a paused Argo rollout. FixControl never starts a deployment.",
    },
    step2: {
      tag: "Gate",
      title: "FixControl opens an approval",
      body: "The pause reaches FixControl as a signed webhook event and opens a deployment gate. Someone on your team reviews the change and the evidence behind it, then approves or rejects.",
    },
    step3: {
      tag: "Signed order",
      title: "The verdict becomes a signed operation",
      body: "FixControl registers a single-use operation: signed with a FixControl key your agent has pinned, addressed to one cluster and one capability, and expiring within minutes. FixControl never opens a connection to deliver it.",
    },
    step4: {
      tag: "Outbound poll",
      title: "The agent collects it — outbound",
      body: "The FC Agent polls FixControl over HTTPS, verifies the signature, the expiry and its own local allowlist, and only then executes the operation against your Kubernetes, Argo, Jenkins or GitLab — with credentials that never left your environment.",
    },
    step5: {
      tag: "Evidence",
      title: "The result comes back for the record",
      body: "The agent reports the outcome to FixControl. The approval, the operation and the result line up in one audit trail — including operations the agent refused.",
    },
  },
  install: {
    eyebrow: "Getting it",
    title: "A signed release you install yourself",
    description:
      "The package is public, the images are signed, and the installer proves its own result against your cluster. Nothing here needs us in the room.",
    linkLabel: "The package on GitHub",
    step1: {
      tag: "Download",
      title: "Take the release, check the checksum",
      body: "The install package is a public release: plain Kubernetes manifests rendered with Kustomize — no Helm — plus the sources of both images, so an air-gapped estate can rebuild them and push to its own registry. Every release ships a SHA-256 checksum next to the tarball.",
    },
    step2: {
      tag: "Verify",
      title: "Check the signature before you run it",
      body: "Both container images are digest-pinned and signed with a keyless Sigstore signature tied to the release workflow, and each carries an attested CycloneDX SBOM. `cosign verify` is one command, and there is no key of ours you have to store or trust.",
    },
    step3: {
      tag: "Configure",
      title: "One values file, and the allowlists are yours",
      body: "Enrolment shows the agent's identity once, assembled and ready to paste. What it deliberately does not fill in are the allowlists — which namespaces, which rollouts, which commands, which egress destinations. FixControl never sends those and cannot widen them.",
    },
    step4: {
      tag: "Prove",
      title: "The installer checks its own work",
      body: "A read-only verify script asserts the result against your live cluster: workloads running on the pinned digest, RBAC positives and negatives, a network policy with no inbound rule and no route to anywhere unnamed, and the admission policy demonstrated by making your API server refuse writes it should refuse.",
    },
  },
  what: {
    eyebrow: "Why it is safe",
    title: "Six properties, all enforced on your side of the wall",
  },
  features: {
    outbound: {
      title: "Outbound-only connection",
      body: "The agent initiates every connection to FixControl over HTTPS. The complete firewall requirement is one egress rule; there are no inbound rules, no VPN, and no tunnel that gives FixControl a way in.",
    },
    private: {
      title: "Private systems stay private",
      body: "Kubernetes, Argo, Jenkins and GitLab keep their existing network posture. None of them needs to be made publicly reachable for FixControl to govern deployments on them.",
    },
    signed: {
      title: "Signed, expiring, single-use operations",
      body: "Every action arrives as an operation signed by FixControl with a key your agent pins, expires within minutes, and can be redeemed exactly once. A replayed or altered operation is refused — and the refusal is recorded.",
    },
    allowlist: {
      title: "You keep local control",
      body: "The agent executes only operations that pass its own allowlists — which namespaces, which rollouts, which capabilities. Those lists live in your environment; FixControl cannot send them and cannot override them. Empty means nothing is allowed.",
    },
    rbac: {
      title: "Least-privilege RBAC",
      body: "The agent’s Kubernetes role is minimal: read access to the rollouts you allow, plus its own bookkeeping — no cluster-admin. The component that runs validation is a separate identity that cannot touch rollouts at all.",
    },
    credentials: {
      title: "Credentials stay with you",
      body: "The Kubernetes service account and the GitLab or Jenkins credential live in your cluster’s own secrets, rotated by you. For connections through the agent, FixControl sends an operation and receives a result — it does not hold credentials for your infrastructure.",
    },
  },
  capabilities: {
    eyebrow: "Capabilities & health",
    title: "A closed set of operations, each with its own health",
    description:
      "The agent advertises what it may do, FixControl narrows every operation to one cluster and one capability, and connectivity is tracked as first-class health — a silent agent shows up as degraded, not as green.",
    collect: {
      name: "Collect signed operations (outbound poll)",
      body: "The agent polls FixControl on a short interval over HTTPS and picks up only operations addressed to its cluster and its granted capabilities.",
    },
    ciGates: {
      name: "Approve or reject a paused CI deployment",
      body: "Where configured during your pilot: GitLab deployment approvals and manual jobs, and Jenkins input steps. The verdict executes through your CI host’s own primitive. (GitHub Actions environment reviews don’t need the agent — api.github.com is reachable directly.)",
    },
    promote: {
      name: "Promote a paused Argo rollout",
      body: "Where configured. After an approved gate, the agent promotes through the path you chose — including a GitOps commit that your own in-cluster tooling verifies before promoting.",
    },
    validate: {
      name: "Run validation in your own cluster",
      body: "Where configured during your pilot, a proposed change is validated by a runner inside your Kubernetes environment, with the same evidence contract as FixControl’s isolated Docker validation.",
    },
    inbound: {
      name: "Inbound access to your network",
      body: "Doesn’t exist. The agent has no listener, no service, no ingress. FixControl cannot connect to the agent — only the other way around.",
    },
    arbitrary: {
      name: "Arbitrary commands",
      body: "Not possible. The operation vocabulary is closed — approve or reject a CI deployment, promote, abort or read an Argo rollout, run or cancel a validation. There is no “run this command” operation, and the agent refuses anything outside the vocabulary.",
    },
  },
  failures: {
    eyebrow: "Failure behavior",
    title: "If FixControl is unreachable, nothing in your cluster changes",
    noteTitle: "Outages degrade loudly, never dangerously",
    noteBody:
      "Your workloads keep running: the agent only acts on signed operations it fetches itself — and every execution verdict is a recorded human decision — so an outage on FixControl’s side simply means no new operations are collected. Pending operations expire within minutes rather than executing late — an approval decided at 10:00 never promotes something at 16:00. The missed heartbeat is visible as degraded connectivity health, and refused or expired operations are recorded, not dropped.",
  },
  faq: {
    eyebrow: "Security FAQ",
    title: "The questions your security team will ask",
    q1: {
      q: "Does FixControl need inbound access to our cluster or CI?",
      a: "No. The agent connects outbound to FixControl over HTTPS — typically port 443 through your existing egress or proxy rules, which stay under your control. There are no inbound firewall rules, no VPN and no reverse tunnel.",
    },
    q2: {
      q: "Do we need to expose Kubernetes, Argo, Jenkins or GitLab publicly?",
      a: "No. For connections through the FC Agent they keep their private network posture. The agent reaches them from inside your network, and reaches FixControl outbound.",
    },
    q3: {
      q: "Does the agent need cluster-admin?",
      a: "No. Its Kubernetes role is read access to the rollouts you allowlist plus its own bookkeeping — no cluster-admin, and by default no write access to rollouts at all. Validation runs under a separate identity that cannot reach rollouts, with an admission policy that blocks writes outside its own test namespaces.",
    },
    q4: {
      q: "Where are our credentials stored?",
      a: "In your environment. The agent authenticates to Kubernetes with its in-cluster service account, and holds your GitLab or Jenkins credential in a secret in your cluster — deliberately separate from the agent’s own FixControl identity, so they rotate independently. For connections through the agent, FixControl stores no credential for your infrastructure.",
    },
    q5: {
      q: "What happens if FixControl is unavailable?",
      a: "Nothing, to your workloads. Deployments your team runs continue as they always did; only new FixControl-governed approvals wait. Operations that were already pending expire within minutes instead of executing late.",
    },
    q6: {
      q: "Can FixControl deploy to production on its own?",
      a: "No. FixControl never starts a deployment. A pipeline pauses on a step your team configured; FixControl records a human decision on that pause; and the signed operation the agent executes is that decision — nothing more. By default nothing moves without a recorded human decision, and each operation leaves an audit record.",
    },
  },
  related: {
    validation: {
      label: "Change validation",
      description: "Isolated Docker validation, and validation in your own cluster.",
    },
    security: {
      label: "Security",
      description: "Tenant isolation, governance, evidence and audit.",
    },
    argo: {
      label: "Argo integration",
      description: "Deployment gates with human-approved promotion.",
    },
    docs: {
      label: "FC Agent admin guide",
      description: "The full install doc: egress rules, RBAC, local allowlists, enrolment and troubleshooting.",
    },
  },
  cta: {
    title: "See a private deployment governed end to end",
    description:
      "Book a demo and watch a pause in a private pipeline reach an approval — and the approval reach your cluster — without a single inbound connection.",
    primary: "Book a demo",
    secondary: "Talk to us",
  },
};

export const intgFcAgentNl: typeof intgFcAgentEn = {
  meta: {
    title: "FC Agent — koppel private infrastructuur zonder haar bloot te stellen",
    description:
      "De FC Agent draait binnen je eigen omgeving en verbindt uitgaand met FixControl over HTTPS. Kubernetes, Argo, Jenkins en GitLab blijven privé: geen inkomende toegang, getekende single-use operaties, allowlists aan jouw kant en least-privilege RBAC. Download het ondertekende pakket en installeer het zelf.",
  },
  hero: {
    eyebrow: "FC Agent · Privé-infrastructuur",
    title: "Je infrastructuur blijft privé.",
    highlight: "De FC Agent belt alleen naar buiten.",
    subtitle:
      "De FC Agent is een kleine service die je in je eigen omgeving draait. Hij verbindt uitgaand met FixControl over HTTPS en voert alleen getekende, aflopende, single-use operaties uit — zodat FixControl deployments en validatie op Kubernetes, Argo, Jenkins en GitLab kan governen zonder dat een van die systemen publiek bereikbaar hoeft te worden. Je downloadt het pakket, verifieert de handtekening en installeert het zelf.",
    primaryCta: "Boek een demo",
    secondaryCta: "Lees het securitymodel",
    bullet1: "Alleen uitgaand — één egress-regel",
    bullet2: "Agent-koppelingen: credentials blijven bij jou",
    bullet3: "Jij bepaalt de lokale allowlists",
  },
  diagram: {
    eyebrow: "Het verbindingsmodel",
    title: "Eén uitgaande verbinding, niets inkomend",
    cloud: "FixControl Cloud",
    cloudSub: "poorten, goedkeuringen, bewijs, audit",
    arrow: "Uitgaand HTTPS — geïnitieerd door de agent",
    agent: "FC Agent",
    agentSub: "draait in jouw omgeving",
    k8s: "Kubernetes",
    argo: "Argo Rollouts",
    ci: "Jenkins / GitLab",
    caption:
      "De volledige firewall-eis is één egress-regel. Er zijn geen inkomende regels, geen VPN en geen tunnel — de agent verifieert elke operatie lokaal en voert haar daarna binnen je eigen netwerk uit.",
  },
  how: {
    eyebrow: "Hoe het werkt",
    title: "Van een gepauzeerde pipeline naar een uitgevoerde beslissing",
    step1: {
      tag: "Pauze",
      title: "Je pipeline pauzeert zichzelf",
      body: "Een deployment bereikt een stap die je team zelf al liet wachten: een GitLab-deployment-approval of handmatige job, een Jenkins-input-stap, een GitHub Actions environment review, of een gepauzeerde Argo-rollout. FixControl start nooit een deployment.",
    },
    step2: {
      tag: "Poort",
      title: "FixControl opent een goedkeuring",
      body: "De pauze bereikt FixControl als een ondertekend webhook-event en opent een deployment-poort. Iemand van je team beoordeelt de wijziging en het bewijs erachter, en keurt goed of wijst af.",
    },
    step3: {
      tag: "Getekende opdracht",
      title: "Het verdict wordt een getekende operatie",
      body: "FixControl registreert een single-use operatie: getekend met een FixControl-sleutel die jouw agent heeft gepind, geadresseerd aan één cluster en één capability, en verlopend binnen minuten. FixControl opent nooit een verbinding om haar af te leveren.",
    },
    step4: {
      tag: "Uitgaande poll",
      title: "De agent haalt haar op — uitgaand",
      body: "De FC Agent pollt FixControl over HTTPS, verifieert de handtekening, de vervaltijd en zijn eigen lokale allowlist, en voert de operatie pas daarna uit tegen jouw Kubernetes, Argo, Jenkins of GitLab — met credentials die je omgeving nooit hebben verlaten.",
    },
    step5: {
      tag: "Bewijs",
      title: "Het resultaat komt terug voor het dossier",
      body: "De agent meldt de uitkomst terug aan FixControl. De goedkeuring, de operatie en het resultaat sluiten op elkaar aan in één audittrail — inclusief operaties die de agent weigerde.",
    },
  },
  install: {
    eyebrow: "Zo krijg je hem",
    title: "Een ondertekende release die je zelf installeert",
    description:
      "Het pakket is openbaar, de images zijn ondertekend, en de installer bewijst zijn eigen resultaat tegen jouw cluster. Hier is niemand van ons bij nodig.",
    linkLabel: "Het pakket op GitHub",
    step1: {
      tag: "Downloaden",
      title: "Haal de release op en controleer de checksum",
      body: "Het installatiepakket is een openbare release: gewone Kubernetes-manifests gerenderd met Kustomize — geen Helm — plus de broncode van beide images, zodat een air-gapped omgeving ze zelf kan bouwen en naar een eigen registry kan pushen. Elke release levert een SHA-256-checksum naast het archief.",
    },
    step2: {
      tag: "Verifiëren",
      title: "Controleer de handtekening vóór je hem draait",
      body: "Beide containerimages zijn digest-gepind en ondertekend met een keyless Sigstore-handtekening die aan de release-workflow hangt, en dragen een geattesteerde CycloneDX-SBOM. `cosign verify` is één commando, en er is geen sleutel van ons die je hoeft te bewaren of vertrouwen.",
    },
    step3: {
      tag: "Instellen",
      title: "Eén values-bestand, en de allowlists zijn van jou",
      body: "Bij enrolment zie je de identiteit van de agent één keer, samengesteld en klaar om te plakken. Wat er bewust níét in staat zijn de allowlists — welke namespaces, welke rollouts, welke commando's, welke egress-bestemmingen. FixControl stuurt die nooit mee en kan ze niet verruimen.",
    },
    step4: {
      tag: "Bewijzen",
      title: "De installer controleert zijn eigen werk",
      body: "Een read-only verify-script toetst het resultaat aan je draaiende cluster: workloads op de gepinde digest, RBAC-rechten én -weigeringen, een netwerkpolicy zonder inkomende regel en zonder route naar iets ongenoemds, en de admission policy aangetoond doordat je API-server writes weigert die hij hoort te weigeren.",
    },
  },
  what: {
    eyebrow: "Waarom het veilig is",
    title: "Zes eigenschappen, allemaal afgedwongen aan jouw kant van de muur",
  },
  features: {
    outbound: {
      title: "Alleen uitgaande verbinding",
      body: "De agent initieert elke verbinding met FixControl over HTTPS. De volledige firewall-eis is één egress-regel; er zijn geen inkomende regels, geen VPN en geen tunnel die FixControl een weg naar binnen geeft.",
    },
    private: {
      title: "Private systemen blijven privé",
      body: "Kubernetes, Argo, Jenkins en GitLab behouden hun bestaande netwerkopstelling. Geen van deze systemen hoeft publiek bereikbaar te worden gemaakt om deployments erop te governen.",
    },
    signed: {
      title: "Getekende, aflopende, single-use operaties",
      body: "Elke actie arriveert als een operatie die FixControl tekent met een sleutel die jouw agent pint, binnen minuten verloopt en precies één keer verzilverd kan worden. Een herhaalde of aangepaste operatie wordt geweigerd — en die weigering wordt vastgelegd.",
    },
    allowlist: {
      title: "Jij houdt lokale controle",
      body: "De agent voert alleen operaties uit die door zijn eigen allowlists komen — welke namespaces, welke rollouts, welke capabilities. Die lijsten leven in jouw omgeving; FixControl kan ze niet meesturen en niet overschrijven. Leeg betekent: niets toegestaan.",
    },
    rbac: {
      title: "Least-privilege RBAC",
      body: "De Kubernetes-rol van de agent is minimaal: leestoegang tot de rollouts die jij toestaat, plus zijn eigen administratie — geen cluster-admin. Het onderdeel dat validatie draait is een aparte identiteit die rollouts helemaal niet kan aanraken.",
    },
    credentials: {
      title: "Credentials blijven bij jou",
      body: "Het Kubernetes-serviceaccount en de GitLab- of Jenkins-credential leven in de eigen secrets van je cluster en worden door jou geroteerd. Voor verbindingen via de agent stuurt FixControl een operatie en ontvangt een resultaat — het bewaart geen credentials voor jouw infrastructuur.",
    },
  },
  capabilities: {
    eyebrow: "Capabilities & health",
    title: "Een gesloten set operaties, elk met eigen health",
    description:
      "De agent adverteert wat hij mag, FixControl vernauwt elke operatie tot één cluster en één capability, en connectiviteit wordt als volwaardige health bijgehouden — een stille agent verschijnt als gedegradeerd, niet als groen.",
    collect: {
      name: "Getekende operaties ophalen (uitgaande poll)",
      body: "De agent pollt FixControl met een kort interval over HTTPS en pakt alleen operaties op die aan zijn cluster en zijn toegekende capabilities zijn geadresseerd.",
    },
    ciGates: {
      name: "Een gepauzeerde CI-deployment goedkeuren of afwijzen",
      body: "Waar geconfigureerd tijdens je pilot: GitLab-deployment-approvals en handmatige jobs, en Jenkins-input-stappen. Het verdict wordt uitgevoerd via het eigen primitief van je CI-host. (GitHub Actions environment reviews hebben de agent niet nodig — api.github.com is direct bereikbaar.)",
    },
    promote: {
      name: "Een gepauzeerde Argo-rollout promoten",
      body: "Waar geconfigureerd. Na een goedgekeurde poort promoot de agent via het pad dat jij koos — inclusief een GitOps-commit die je eigen in-cluster tooling verifieert voordat er gepromoot wordt.",
    },
    validate: {
      name: "Validatie draaien in je eigen cluster",
      body: "Waar geconfigureerd tijdens je pilot wordt een voorgestelde wijziging gevalideerd door een runner binnen jouw Kubernetes-omgeving, met hetzelfde bewijscontract als FixControls geïsoleerde Docker-validatie.",
    },
    inbound: {
      name: "Inkomende toegang tot je netwerk",
      body: "Bestaat niet. De agent heeft geen listener, geen service, geen ingress. FixControl kan geen verbinding maken met de agent — alleen andersom.",
    },
    arbitrary: {
      name: "Willekeurige commando’s",
      body: "Niet mogelijk. Het operatie-vocabulaire is gesloten — een CI-deployment goedkeuren of afwijzen, een Argo-rollout promoten, afbreken of uitlezen, een validatie starten of annuleren. Er bestaat geen “voer dit commando uit”-operatie, en de agent weigert alles buiten het vocabulaire.",
    },
  },
  failures: {
    eyebrow: "Gedrag bij storing",
    title: "Is FixControl onbereikbaar, dan verandert er niets in je cluster",
    noteTitle: "Storingen degraderen luid, nooit gevaarlijk",
    noteBody:
      "Je workloads blijven draaien: de agent handelt alleen op getekende operaties die hij zelf ophaalt — en elk uitvoerings-verdict is een vastgelegde menselijke beslissing — dus een storing aan FixControls kant betekent simpelweg dat er geen nieuwe operaties worden opgehaald. Openstaande operaties verlopen binnen minuten in plaats van te laat uitgevoerd te worden — een goedkeuring van 10:00 promoot nooit iets om 16:00. De gemiste heartbeat is zichtbaar als gedegradeerde connectiviteits-health, en geweigerde of verlopen operaties worden vastgelegd, niet weggegooid.",
  },
  faq: {
    eyebrow: "Security-FAQ",
    title: "De vragen die je securityteam gaat stellen",
    q1: {
      q: "Heeft FixControl inkomende toegang tot ons cluster of onze CI nodig?",
      a: "Nee. De agent verbindt uitgaand met FixControl over HTTPS — doorgaans poort 443 via je bestaande egress- of proxyregels, die onder jouw beheer blijven. Er zijn geen inkomende firewallregels, geen VPN en geen omgekeerde tunnel.",
    },
    q2: {
      q: "Moeten we Kubernetes, Argo, Jenkins of GitLab publiek toegankelijk maken?",
      a: "Nee. Voor verbindingen via de FC Agent behouden ze hun private netwerkopstelling. De agent bereikt ze vanaf de binnenkant van je netwerk, en bereikt FixControl uitgaand.",
    },
    q3: {
      q: "Heeft de agent cluster-admin nodig?",
      a: "Nee. Zijn Kubernetes-rol is leestoegang tot de rollouts die jij allowlist plus zijn eigen administratie — geen cluster-admin, en standaard helemaal geen schrijftoegang tot rollouts. Validatie draait onder een aparte identiteit die rollouts niet kan bereiken, met een admission-policy die schrijfacties buiten de eigen testnamespaces blokkeert.",
    },
    q4: {
      q: "Waar staan onze credentials?",
      a: "In jouw omgeving. De agent authenticeert bij Kubernetes met zijn in-cluster serviceaccount en bewaart je GitLab- of Jenkins-credential in een secret in je cluster — bewust gescheiden van de eigen FixControl-identiteit van de agent, zodat ze onafhankelijk roteren. Voor verbindingen via de agent bewaart FixControl geen credential voor jouw infrastructuur.",
    },
    q5: {
      q: "Wat gebeurt er als FixControl niet beschikbaar is?",
      a: "Niets, voor je workloads. Deployments die je team zelf draait gaan gewoon door; alleen nieuwe door FixControl gegovernde goedkeuringen wachten. Operaties die al openstonden verlopen binnen minuten in plaats van te laat uitgevoerd te worden.",
    },
    q6: {
      q: "Kan FixControl op eigen houtje naar productie deployen?",
      a: "Nee. FixControl start nooit een deployment. Een pipeline pauzeert op een stap die je team configureerde; FixControl legt een menselijke beslissing op die pauze vast; en de getekende operatie die de agent uitvoert ís die beslissing — meer niet. Standaard beweegt er niets zonder een vastgelegde menselijke beslissing, en elke operatie laat een auditspoor achter.",
    },
  },
  related: {
    validation: {
      label: "Change-validatie",
      description: "Geïsoleerde Docker-validatie, en validatie in je eigen cluster.",
    },
    security: {
      label: "Security",
      description: "Isolatie per organisatie, governance, bewijs en audit.",
    },
    argo: {
      label: "Argo-integratie",
      description: "Deployment-poorten met door een mens goedgekeurde promotie.",
    },
    docs: {
      label: "FC Agent-beheerdocumentatie",
      description: "De volledige installatiedoc: egress-regels, RBAC, lokale allowlists, enrolment en troubleshooting.",
    },
  },
  cta: {
    title: "Zie een private deployment end-to-end gegoverned",
    description:
      "Boek een demo en zie hoe een pauze in een private pipeline een goedkeuring bereikt — en de goedkeuring je cluster — zonder één inkomende verbinding.",
    primary: "Boek een demo",
    secondary: "Praat met ons",
  },
};
